export const TYPES = Object.freeze({
  BLOG: { type: 'blog', key: 'cms_content_type_blog' },
  PAGE: { type: 'page', key: 'cms_content_type_page' },
  HELPDESK: { type: 'helpdesk', key: 'cms_content_type_helpdesk' },
})

export const STATUSES = Object.freeze({
  DRAFT: { status: 'draft', key: 'cms_content_status_draft' },
  PUBLISHED: { status: 'published', key: 'cms_content_status_published' },
})

// The two faces of cms_content.is_private, as the content overview shows and filters them. Only Blog
// Posts and Pages ever get a Visibility column; a Helpdesk Article is always public.
export const VISIBILITIES = Object.freeze({
  PUBLIC: { visibility: 'public', isPrivate: 0, key: 'cms_content_visibility_public' },
  PRIVATE: { visibility: 'private', isPrivate: 1, key: 'cms_content_visibility_private' },
})

// Character caps of the cms_content_localisation VARCHAR columns. Stated in the AI metadata
// prompt and hard-truncated server-side so an over-long model response can never break a save.
export const CMS_METADATA_MAX_LENGTHS = Object.freeze({
  excerpt: 350,
  metaTitle: 60,
  metaDescription: 160,
})

// Cap on an article title. Well under the width of the cms_content_localisation.title column it is
// stored in: the limit is editorial, not structural, since a title this long already reads badly in
// a card and a search result. Enforced on save, not just in the editor's input, so the API cannot
// carry a longer one past it.
export const CMS_TITLE_MAX_LENGTH = 80

// Request caps for the Ask AI endpoint. Nothing is stored, so these only bound what we forward to
// Gemini: a prompt is a single instruction, and the body context is the text preceding the cursor.
// The title it is sent is capped by CMS_TITLE_MAX_LENGTH, which the save endpoint enforces, so it
// needs no cap of its own.
export const ASK_AI_MAX_LENGTHS = Object.freeze({
  prompt: 2000,
  bodyContext: 20000,
})

// Request cap for the spelling and grammar endpoint. Nothing is stored, so this only bounds what we
// forward to Gemini: the text is an author's selection, a few paragraphs at most.
export const SPELL_CHECK_MAX_TEXT_LENGTH = 5000

// Request cap for the reduce-text endpoint. Nothing is stored, so this only bounds what we forward to
// Gemini: the text is an author's selection, a few paragraphs at most.
export const REDUCE_TEXT_MAX_LENGTH = 5000

// Request cap for the simplify-text endpoint. Nothing is stored, so this only bounds what we forward
// to Gemini: the text is an author's selection, a few paragraphs at most.
export const SIMPLIFY_TEXT_MAX_LENGTH = 5000

// Request caps for the extend-text endpoint. Nothing is stored, so these only bound what we forward
// to Gemini: the text is the author's selection, and each context window is the text on one side of
// it, sent so the expansion flows with its surroundings.
export const EXTEND_TEXT_MAX_LENGTHS = Object.freeze({
  text: 5000,
  context: 2000,
})

// Request cap for the AI translation endpoint, applied to the combined length of the strings sent in
// one call. Nothing is stored, so this only bounds what we forward to Gemini: the strings are the
// Title and (from the block round-trip on) the blocks of a single article, so one enormous article
// cannot run up a runaway bill.
export const CMS_TRANSLATE_MAX_TEXT_LENGTH = 50000

// Why a visitor was refused a piece of Private Content. The public detail endpoints return one of
// these instead of the content, and the Access Wall renders the matching message: 'login_required' for
// an anonymous visitor (who is offered a login button), 'not_linked' for someone who is logged in but
// is not a member of the platform (for whom there is nothing useful to offer).
export const CMS_ACCESS_DENIED = Object.freeze({
  LOGIN_REQUIRED: 'login_required',
  NOT_LINKED: 'not_linked',
})

export const typeEquals = (value, type) => value === type.type
export const statusEquals = (value, status) => value === status.status
export const visibilityEquals = (value, visibility) => value === visibility.visibility
